/* eslint-disable indent */
import { useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  GroupLocalStorage,
  StructureAdvancedFilter,
} from '@/entities/structure/group';
import { useLocalStorageState } from '../local-storage.hooks';
import { StructureType } from '@/entities/structure/common';
import { Dayjs } from 'dayjs';
import { CompanyLocalStorage } from '@/entities/structure/company';
import { BranchLocalStorage } from '@/entities/structure/branch';

interface StructureFilterProps {
  structureType: StructureType;
  setCurrentPage: (page: number) => void;
}

export const useStructureFilter = ({
  structureType,
  setCurrentPage,
}: StructureFilterProps) => {
  const [debouncedFilter, setDebouncedFilter] =
    useState<StructureAdvancedFilter>({ key: '' });
  const filtersEmptyRef = useRef<boolean>(true);

  const getStorageKey = () => {
    let key = '';
    let type = '';
    let startDate = '';
    let endDate = '';
    let isWhiteMark = '';
    let distributorId = '';
    let groupId = '';
    let companyId = '';
    let branchId = '';

    switch (structureType) {
      case StructureType.GROUP:
        key = GroupLocalStorage.KEY;
        type = GroupLocalStorage.TYPE;
        startDate = GroupLocalStorage.START_DATE;
        endDate = GroupLocalStorage.END_DATE;
        isWhiteMark = GroupLocalStorage.IS_WHITE_MARK;
        companyId = GroupLocalStorage.COMPANY_ID;
        branchId = GroupLocalStorage.BRANCH_ID;
        break;
      case StructureType.COMPANY:
        key = CompanyLocalStorage.KEY;
        type = CompanyLocalStorage.TYPE;
        startDate = CompanyLocalStorage.START_DATE;
        endDate = CompanyLocalStorage.END_DATE;
        isWhiteMark = CompanyLocalStorage.IS_WHITE_MARK;
        distributorId = CompanyLocalStorage.DISTRIBUTOR_ID;
        groupId = CompanyLocalStorage.GROUP_ID;
        branchId = CompanyLocalStorage.BRANCH_ID;
        break;
      case StructureType.BRANCH:
        key = BranchLocalStorage.KEY;
        type = BranchLocalStorage.TYPE;
        startDate = BranchLocalStorage.START_DATE;
        endDate = BranchLocalStorage.END_DATE;
        isWhiteMark = BranchLocalStorage.IS_WHITE_MARK;
        distributorId = BranchLocalStorage.DISTRIBUTOR_ID;
        groupId = BranchLocalStorage.GROUP_ID;
        companyId = BranchLocalStorage.COMPANY_ID;
        break;
      default:
        key = '';
        type = '';
        startDate = '';
        endDate = '';
        isWhiteMark = '';
        distributorId = '';
        groupId = '';
        companyId = '';
        branchId = '';
    }

    return {
      key,
      type,
      startDate,
      endDate,
      isWhiteMark,
      distributorId,
      groupId,
      companyId,
      branchId,
    };
  };

  /**
   * === Filter section ===
   */
  const [key, setKey] = useLocalStorageState({
    key: getStorageKey()?.key,
    removeOnClose: true,
  });

  const [type, setType] = useLocalStorageState({
    key: getStorageKey()?.type,
    removeOnClose: true,
  });

  const [startDate, setStartDate] = useLocalStorageState({
    key: getStorageKey()?.startDate,
    removeOnClose: true,
  });

  const [endDate, setEndDate] = useLocalStorageState({
    key: getStorageKey()?.endDate,
    removeOnClose: true,
  });

  const [isWhiteMark, setIsWhiteMark] = useLocalStorageState({
    key: getStorageKey()?.isWhiteMark,
    removeOnClose: true,
  });

  const [distributorId, setDistributorId] = useLocalStorageState({
    key: getStorageKey()?.distributorId,
    removeOnClose: true,
  });

  const [groupId, setGroupId] = useLocalStorageState({
    key: getStorageKey()?.groupId,
    removeOnClose: true,
  });

  const [companyId, setCompanyId] = useLocalStorageState({
    key: getStorageKey()?.companyId,
    removeOnClose: true,
  });

  const [branchId, setBranchId] = useLocalStorageState({
    key: getStorageKey()?.branchId,
    removeOnClose: true,
  });

  useEffect(() => {
    setDebouncedFilter((prev) => ({
      ...prev,
      key: key || '',
      type,
      startDate,
      endDate,
      isWhiteMark: isWhiteMark === 'true',
      distributorId,
      groupId,
      companyId,
      branchId,
    }));

    filtersEmptyRef.current =
      !key &&
      !type &&
      !startDate &&
      !endDate &&
      isWhiteMark !== 'true' &&
      !distributorId &&
      !groupId &&
      !companyId &&
      !branchId;
  }, [
    key,
    type,
    startDate,
    endDate,
    isWhiteMark,
    distributorId,
    groupId,
    companyId,
    branchId,
  ]);

  const searchByKey = (value: string) => {
    setKey(value);
    debounce((value: string) => {
      setDebouncedFilter((prev) => ({ ...prev, key: value }));
    }, 1000);
  };

  const searchByTypes = (value: string) => {
    setType(value);

    switch (value) {
      case '1':
        setDebouncedFilter((prev) => ({
          ...prev,
          isDistributor: true,
          isBranch: false,
        }));
        break;
      case '2':
        setDebouncedFilter((prev) => ({
          ...prev,
          isDistributor: false,
          isBranch: true,
        }));
        break;
      case '3':
        setDebouncedFilter((prev) => ({
          ...prev,
          isDistributor: true,
          isBranch: true,
        }));
        break;
      default:
        setDebouncedFilter((prev) => ({
          ...prev,
          isDistributor: undefined,
          isBranch: undefined,
        }));
        break;
    }
  };

  const searchBeginDate = (value: Dayjs) => {
    setStartDate(value?.format('YYYY-MM-DD'));
    setDebouncedFilter((prev) => ({
      ...prev,
      startDate: value?.format('YYYY-MM-DD'),
    }));
  };

  const searchEndDate = (value: Dayjs) => {
    setEndDate(value?.format('YYYY-MM-DD'));
    setDebouncedFilter((prev) => ({
      ...prev,
      endDate: value?.format('YYYY-MM-DD'),
    }));
  };

  const searchWhiteMark = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setIsWhiteMark(value.toString());
    setDebouncedFilter((prev) => ({ ...prev, isWhiteMark: value }));
  };

  const searchByDistributor = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDistributorId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, distributorId: e.target.value }));
  };

  const searchByGroup = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGroupId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, groupId: e.target.value }));
  };

  const searchByCompany = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, companyId: e.target.value }));
  };

  const searchByBranch = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBranchId(e.target.value);
    setDebouncedFilter((prev) => ({ ...prev, branchId: e.target.value }));
  };

  const clearFilters = () => {
    setKey('');
    setType('');
    setStartDate('');
    setEndDate('');
    setIsWhiteMark('false');
    setDistributorId('');
    setGroupId('');
    setCompanyId('');
    setBranchId('');
    setCurrentPage(1);
  };
  /**
   * === END OF Filter section ===
   */

  return {
    debouncedFilter,
    setDebouncedFilter,
    filtersEmpty: filtersEmptyRef.current,
    clearFilters,
    key,
    type,
    startDate,
    endDate,
    isWhiteMark,
    distributorId,
    groupId,
    companyId,
    branchId,
    searchByKey,
    searchByTypes,
    searchBeginDate,
    searchEndDate,
    searchWhiteMark,
    searchByDistributor,
    searchByGroup,
    searchByCompany,
    searchByBranch,
  };
};
